import i18n from '@/lang/i18n'
import { store } from '@/store/store'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import CaseStatusRenderer from '@/cellRenderers/registry/CaseStatusRenderer.vue'
import { CASE_STATES, caseState } from '@/api/resources/entityState/case-state'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import { CellClickedEvent, ColDef, KeyCreatorParams, SetFilterValuesFuncParams } from '@ag-grid-enterprise/all-modules'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import AttachmentCountRenderer from '@/cellRenderers/common/AttachmentCountRenderer.vue'
import { isNil } from 'lodash'
import { ModuleOperationGuard } from '@/api/operations/guards/module-operation-guard'
import AiAnalysisStatusIconRenderer
    from '@/cellRenderers/common/ai-analysis-status/renderer/ai-analysis-status-icon-renderer.vue'
import CommonSetFilterRenderer from '@/cellRenderers/common/CommonSetFilterRenderer.vue'
import { aiAnalysisState } from '@/api/resources/entityState/ai-analysis-state'
import AiAnalysisStatusIcon from '@/cellRenderers/common/ai-analysis-status/ai-analysis-status-icon.vue'
import { VtGridValues } from '@/grid/vt-grid-values'

const diagnosisCaseGridMasterChildrenCaseColumnDefs = (onIdCellClicked: (event: CellClickedEvent) => void): (ColDef | ExportedColDef | ExportedColGroupDef)[] => ([
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.labName'),
        field: 'laboratory.labName',
        type: AgGridColumnType.LABORATORIES,
        exportType: AgGridExportType.TEXT,
        suppressSizeToFit: true,
        width: 110
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.casCasenumber'),
        field: 'casCasenumber',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        suppressSizeToFit: true,
        width: 110,
        onCellClicked: onIdCellClicked
    },
    {
        headerName: i18n.tc('global.status'),
        field: 'status',
        type: AgGridColumnType.COMMON_CASE_STATUS,
        exportField: 'status.name',
        exportType: AgGridExportType.TEXT,
        width: 110,
        suppressSizeToFit: true,
        cellRendererFramework: CaseStatusRenderer,
        filterParams: {
            suppressMiniFilter: true,
            newRowsAction: 'keep',
            cellRendererFramework: CaseStatusRenderer,
            cellRendererParams: {
                states: caseState.states,
                isFilterCellRenderer: true
            },
            values: caseState.states.map(state => state.code).filter(code => code !== CASE_STATES.DRAFT.code),
            debounceMs: 1000
        }
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.casRegistrationDate'),
        field: 'casRegistrationDate',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        width: 110,
        suppressSizeToFit: true
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.casInformedDate'),
        field: 'casInformedDate',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        width: 110,
        suppressSizeToFit: true
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.casTranscriptionDate'),
        field: 'casTranscriptionDate',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        width: 110,
        suppressSizeToFit: true
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.casValidationDate'),
        field: 'casValidationDate',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        width: 110,
        suppressSizeToFit: true
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.priority'),
        colId: 'priority.level',
        field: 'priority',
        type: AgGridColumnType.PRIORITY,
        exportField: 'priority.name',
        exportType: AgGridExportType.TEXT,
        sortable: false
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.globalDelay'),
        colId: 'globalTargetResponseDate',
        field: 'globalDelay',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.NUMBER,
        filter: false,
        width: 110,
        suppressSizeToFit: true,
        cellRenderer: ({ data }: ICellRendererParams) => {
            const delay = data?.globalDelay
            if (!isNil(delay)) {
                const delayAbs = Math.abs(delay)
                return i18n.tc(
                    `diagnosis.grid.caseGrid.delay.${delay > 0 ? 'positive' : 'negative'}`,
                    delayAbs > 1 ? 2 : 1,
                    { delay: delayAbs })
            }
            return ''
        }
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.diagnosticDelay'),
        colId: 'diagnosticTargetResponseDate',
        field: 'diagnosticDelay',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.NUMBER,
        filter: false,
        width: 110,
        suppressSizeToFit: true,
        cellRenderer: ({ data }: ICellRendererParams) => {
            const delay = data?.diagnosticDelay
            if (!isNil(delay)) {
                const delayAbs = Math.abs(delay)
                return i18n.tc(
                    `diagnosis.grid.caseGrid.delay.${delay > 0 ? 'positive' : 'negative'}`,
                    delayAbs > 1 ? 2 : 1,
                    { delay: delayAbs })
            }
            return ''
        }
    },
    {
        headerName: i18n.tc('global.externalReference'),
        field: 'externalReference',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.samplesCount'),
        field: 'samplesCount',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.INTEGER,
        filter: false,
        sortable: false,
        width: 110,
        suppressSizeToFit: true
    },
    {
        filter: 'agNumberColumnFilter',
        cellRendererFramework: AttachmentCountRenderer,
        headerName: i18n.tc('grid.attachments'),
        headerTooltip: i18n.tc('grid.attachments'),
        field: 'attachmentsCount',
        type: AgGridColumnType.ATTACHMENT_COUNT,
        exportType: AgGridExportType.INTEGER,
        width: 110,
        suppressSizeToFit: true,
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-paperclip')
        }
    },
    {
        headerName: i18n.tc('grid.hasAlarms'),
        headerTooltip: i18n.tc('grid.hasAlarms'),
        field: 'incidentsCount',
        type: AgGridColumnType.INCIDENT,
        exportType: AgGridExportType.INTEGER,
        filter: false,
        width: 110,
        suppressSizeToFit: true,
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-exclamation-triangle')
        }
    },
    // TODO: Poner notificaciones diagnósticas
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.notifications'),
        headerTooltip: i18n.tc('diagnosis.grid.caseGrid.notifications'),
        field: 'attachmentsCount',
        type: AgGridColumnType.ATTACHMENT_COUNT,
        exportType: AgGridExportType.INTEGER,
        filter: false,
        width: 110,
        suppressSizeToFit: true,
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-envelope')
        }
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.pendingAssays'),
        headerTooltip: i18n.tc('diagnosis.grid.caseGrid.pendingAssays'),
        field: 'pendingAssays',
        type: AgGridColumnType.ATTACHMENT_COUNT,
        exportType: AgGridExportType.NUMBER,
        filter: 'agNumberColumnFilter',
        width: 110,
        suppressSizeToFit: true,
        headerComponentParams: {
            template: buildIconHeaderTemplate('fas fa-flask ')
        }
    },
    {
        headerName: i18n.tc('registry.btn.consultation'),
        headerTooltip: i18n.tc('registry.btn.consultation'),
        field: 'caseOfReference',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.BOOLEAN,
        filter: false,
        hide: false,
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-glasses')
        },
        cellRenderer: ({ value }: ICellRendererParams) => value === true ? '<i class="success far fa-check"/>' : ''
    },
    {
        headerName: i18n.tc('diagnosis.aiInformation'),
        headerComponentParams: {
            template: buildIconHeaderTemplate('fas fa-brain')
        },
        headerTooltip: i18n.tc('diagnosis.aiInformation'),
        field: 'analysisStatusAI',
        colId: 'analysisStatusAI.code',
        keyCreator: (params: KeyCreatorParams): string => params.value.code,
        exportType: AgGridExportType.TEXT,
        maxWidth: 80,
        cellRendererFramework: AiAnalysisStatusIconRenderer,
        filterParams: {
            suppressMiniFilter: true,
            cellRendererFramework: CommonSetFilterRenderer,
            cellRendererParams: {
                states: aiAnalysisState.states,
                component: AiAnalysisStatusIcon
            },
            values: (params: SetFilterValuesFuncParams): void => VtGridValues.entityStatusStateValues(params, aiAnalysisState.states),
            debounceMs: 1000
        }
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.pathologist'),
        colId: 'pathologist.normalizedFullName',
        field: 'pathologist.fullName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 110,
        suppressSizeToFit: true
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.resident'),
        colId: 'resident.normalizedFullName',
        field: 'resident.fullName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.administrative'),
        colId: 'administrative.normalizedFullName',
        field: 'administrative.fullName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT
    }
])

export const diagnosisCaseGridMasterColumnDefs =
    (
        onIdCellClicked: (event: CellClickedEvent) => void,
        onExpandClicked: (event: CellClickedEvent) => void
    ): (ColDef | ExportedColDef | ExportedColGroupDef)[] => ([
        {
            type: AgGridColumnType.MASTER_DETAIL,
            colId: 'expand.diagnosis',
            onCellClicked: onExpandClicked
        },
        { type: AgGridColumnType.CUSTOM_CHECKBOX },
        {
            headerName: i18n.tc('entities.case.case'),
            colId: 'case',
            children: ModuleOperationGuard.canAccessBilling ? diagnosisCaseGridMasterChildrenCaseColumnDefs(onIdCellClicked).concat([{
                type: AgGridColumnType.HAS_UNAUTHORIZED_BILLABLE_CONCEPTS,
                exportType: AgGridExportType.BOOLEAN,
                field: 'hasUnauthorizedBillableConcepts',
                flex: 1,
                minWidth: 55
            }]) : diagnosisCaseGridMasterChildrenCaseColumnDefs(onIdCellClicked)
        },
        {
            headerName: i18n.tc('entities.incident.coreEntities.request'),
            children: [
                {
                    headerName: i18n.tc('diagnosis.grid.caseGrid.requestLabel'),
                    field: 'requestLabel',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    minWidth: 110,
                    suppressSizeToFit: true
                },
                {
                    headerName: i18n.tc('diagnosis.grid.caseGrid.casRequestDate'),
                    field: 'casRequestDate',
                    type: AgGridColumnType.DATETIME,
                    exportType: AgGridExportType.DATETIME,
                    minWidth: 110,
                    suppressSizeToFit: true
                },
                {
                    headerName: i18n.tc('registry.cases.unit'),
                    field: 'physicianDepartment.name',
                    type: AgGridColumnType.DEPARTMENTS,
                    exportType: AgGridExportType.TEXT
                },
                {
                    headerName: i18n.tc('global.center'),
                    field: 'physicianDepartment.center.name',
                    type: AgGridColumnType.CENTERS,
                    exportType: AgGridExportType.TEXT
                },
                {
                    headerName: i18n.tc('component.caseCardModal.professional'),
                    colId: 'physicianPetitioner.normalizedFullName',
                    field: 'physicianPetitioner.fullName',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT
                }
            ]
        },
        {
            headerName: i18n.tc('global.patient'),
            colId: 'patient',
            children: [
                {
                    headerValueGetter: () => store.getters.parameters.global?.mainPatientIdentifierType.name,
                    colId: 'patient.personIdentifier',
                    field: 'patient.mainPatientIdentifier.paiIdentifier',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    filterParams: {
                        filterOptions: ['equals'],
                        defaultOption: 'equals',
                        newRowsAction: 'keep'
                    },
                    sortable: false
                },
                {
                    headerValueGetter: () => store.getters.parameters.global?.secondaryPatientIdentifierType.name,
                    colId: 'patient.centerIdentifier',
                    field: 'patient.secondaryPatientIdentifier.paiIdentifier',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    filterParams: {
                        filterOptions: ['equals'],
                        defaultOption: 'equals',
                        newRowsAction: 'keep'
                    },
                    sortable: false
                },
                {
                    headerName: i18n.tc('grid.patName'),
                    colId: 'patient.patName',
                    field: 'patient.name',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    sortable: false
                },
                {
                    headerName: i18n.tc('grid.patMiddleName'),
                    colId: 'patient.patMiddleName',
                    field: 'patient.middleName',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    sortable: false
                },
                {
                    headerName: i18n.tc('grid.patSurname1'),
                    colId: 'patient.patSurname1',
                    field: 'patient.surname1',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    sortable: false
                },
                {
                    headerName: i18n.tc('grid.patSurname2'),
                    colId: 'patient.patSurname2',
                    field: 'patient.surname2',
                    type: AgGridColumnType.TEXT,
                    exportType: AgGridExportType.TEXT,
                    sortable: false
                },
                {
                    headerName: i18n.tc('diagnosis.grid.caseGrid.gender'),
                    colId: 'patient.gender.id',
                    field: 'patient.gender.name',
                    type: 'genders',
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    width: 110,
                    suppressSizeToFit: true
                },
                {
                    headerName: i18n.tc('global.birthday'),
                    field: 'patient.birthdate',
                    type: AgGridColumnType.DATE,
                    exportType: AgGridExportType.DATE,
                    sortable: false
                }
            ]
        }
    ])
